import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

const ProposalSubmissionForm = ({
  dealInterest,
  deal,
  onSubmitProposal,
  onCancel,
  loading,
  demoSecretMenuEnabled
}) => {
  const intl = useIntl();
  const DEMO_SCENARIO_QUESTION_RESPONSES = {
    "What is your experience working with high-net-worth clients and comprehensive financial planning?":
      "I have 10+ years of experience serving high-net-worth households with comprehensive planning, portfolio construction, and retirement income strategies. My current practice is primarily fee-based, and I work closely with clients on long-term goals, tax-aware planning, and multi-generational wealth discussions.",
    "Describe your approach to client communication and how you would maintain relationships during transition.":
      "My transition approach is proactive and relationship-first: joint intro meetings, clear communication milestones, and a structured 90-day follow-up cadence. I prioritize continuity and trust by preserving each client's preferred communication style while ensuring they feel supported at every step.",
    "What is your investment philosophy and how does it align with a fee-based fiduciary model?":
      "My philosophy is goals-based, risk-aligned, and fiduciary-first. I focus on transparent, evidence-informed portfolio decisions and ongoing planning discipline. This aligns closely with a fee-based model where advice quality, client outcomes, and long-term trust are the core priorities.",
    "How do you plan to retain and grow the existing client base after acquisition?":
      "Retention starts with continuity: preserving service cadence, honoring existing relationships, and delivering a clear transition plan. Growth comes from deeper planning engagements, household consolidation, and multi-generational referrals, supported by disciplined onboarding and client service workflows.",
    "What support or resources do you have in place to handle the operational aspects of this practice?":
      "I have established operational infrastructure including CRM workflows, client service SOPs, compliance support, and planning technology. I also have capacity in my team to absorb onboarding, service, and review cycles while maintaining a high-touch client experience.",
    "Describe your experience with retirement income planning and distribution strategies.":
      "Retirement income planning is a core area of my practice. I regularly build distribution frameworks incorporating tax-efficient withdrawal sequencing, cash flow stress testing, Social Security coordination, and portfolio sustainability monitoring.",
    "How would you approach introducing yourself to existing clients during the transition period?":
      "I would use a structured introduction plan: joint meetings with the seller, personalized outreach, and clear communication on what stays consistent. The goal is to transfer trust thoughtfully while demonstrating responsiveness and continuity of advice.",
    "What attracted you to this opportunity and what are your long-term goals for the practice?":
      "I am attracted to the quality of the client relationships, the recurring revenue profile, and the long-term planning focus. My goal is to preserve what has made the practice successful while scaling responsibly through enhanced planning depth and next-generation client development.",
    "Do you have experience managing a team or working with support staff?":
      "Yes. I have experience leading advisor and client-service workflows, setting service standards, and coordinating execution across planning, operations, and relationship management. I value collaborative culture and clear accountability.",
    "What is your timeline for completing an acquisition and transition?":
      "I am prepared to move through diligence promptly and can align to a practical transition timeline that protects client relationships. A phased transition over 12-18 months is typically the best path to preserve continuity and outcomes."
  };

  const DEMO_DEFAULT_PROPOSAL_LETTER = `Dear Seller,

I am writing to express strong interest in your practice opportunity. With over a decade of wealth management experience, I believe I am well-positioned to continue the high standard of client service you have built.

I am committed to a thoughtful transition process centered on client continuity, clear communication, and long-term relationship stewardship. I value fee-based fiduciary advice and would approach this transition with care for your clients, team, and legacy.

I would welcome the opportunity to discuss your goals, transition timeline, and how I can support a successful handoff.

Sincerely,
Interested Buyer`;

  const DEMO_DEFAULT_MEETING_AVAILABILITY = `I am available for an introductory call on weekdays between 9:00 AM and 5:00 PM ET and can be flexible with notice. Preferred windows this week are Tuesday-Thursday, 10:00 AM-2:00 PM ET.`;

  const getDemoAnswerForQuestion = (question) => {
    if (!question || !question.trim()) return '';
    return DEMO_SCENARIO_QUESTION_RESPONSES[question] ||
      'This is a core strength of mine. I bring a disciplined, client-first approach and would be glad to provide specific examples during our discussion.';
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    proposal_letter: '',
    proposal_meeting_availability: '',
    a1: '',
    a2: '',
    a3: '',
    a4: '',
    a5: ''
  });

  const [focusedField, setFocusedField] = useState('');
  const [errors, setErrors] = useState({});

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onCancel]);

  // Prefill demo content for pilot facilitators using scenario_buyer_1 defaults
  useEffect(() => {
    if (!demoSecretMenuEnabled) return;

    setFormData((prev) => {
      const next = { ...prev };

      if (!next.proposal_letter.trim()) {
        next.proposal_letter = DEMO_DEFAULT_PROPOSAL_LETTER;
      }

      if (!next.proposal_meeting_availability.trim()) {
        next.proposal_meeting_availability = DEMO_DEFAULT_MEETING_AVAILABILITY;
      }

      const questionMap = [
        { field: 'a1', question: deal?.question_1 },
        { field: 'a2', question: deal?.question_2 },
        { field: 'a3', question: deal?.question_3 },
        { field: 'a4', question: deal?.question_4 },
        { field: 'a5', question: deal?.question_5 }
      ];

      questionMap.forEach(({ field, question }) => {
        if (!question || !question.trim()) return;
        if (next[field].trim()) return;
        next[field] = getDemoAnswerForQuestion(question);
      });

      return next;
    });
  }, [demoSecretMenuEnabled, deal]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.proposal_letter.trim()) {
      newErrors.proposal_letter = intl.formatMessage({ id: 'succession.buyer_room.proposal_form.validation_proposal_required' });
    }

    if (!formData.proposal_meeting_availability.trim()) {
      newErrors.proposal_meeting_availability = intl.formatMessage({ id: 'succession.buyer_room.proposal_form.validation_availability_required' });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    // Get the list of questions that actually have content
    const validQuestions = getValidQuestions();
    
    // Only validate questions that actually exist
    validQuestions.forEach(question => {
      if (!formData[question.name].trim()) {
        newErrors[question.name] = intl.formatMessage({ id: 'succession.buyer_room.proposal_form.validation_field_required' });
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(1);
    setErrors({});
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      return;
    }

    const input = {
      deal_interest_id: dealInterest.id,
      ...formData
    };

    onSubmitProposal(input);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const getOverallProgress = () => {
    // Get the valid questions that actually exist
    const validQuestions = getValidQuestions();
    
    const allFields = ['proposal_letter', 'proposal_meeting_availability', ...validQuestions.map(q => q.name)];
    const filledFields = allFields.filter(field => formData[field].trim().length > 0);
    return (filledFields.length / allFields.length) * 100;
  };

  const getStepProgress = () => {
    if (currentStep === 1) {
      const step1Fields = ['proposal_letter', 'proposal_meeting_availability'];
      const filledFields = step1Fields.filter(field => formData[field].trim().length > 0);
      return (filledFields.length / step1Fields.length) * 100;
    } else {
      // Get the valid questions that actually exist for step 2
      const validQuestions = getValidQuestions();
      
      const step2Fields = validQuestions.map(q => q.name);
      const filledFields = step2Fields.filter(field => formData[field].trim().length > 0);
      return step2Fields.length > 0 ? (filledFields.length / step2Fields.length) * 100 : 100;
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Helper function to get questions that actually have content
  const getValidQuestions = () => {
    return [
      { name: 'a1', question: deal.question_1 },
      { name: 'a2', question: deal.question_2 },
      { name: 'a3', question: deal.question_3 },
      { name: 'a4', question: deal.question_4 },
      { name: 'a5', question: deal.question_5 }
    ].filter(field => field.question && field.question.trim() !== '');
  };

  const modalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-2 sm:p-4"
      style={{
        zIndex: 2147483647,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)'
      }}
      onClick={handleBackdropClick}
    >
      <div
        className="relative bg-succession-bg-primary rounded-2xl shadow-xl w-full max-w-2xl flex flex-col border border-succession-border"
        style={{ maxHeight: '90vh', overflow: 'hidden' }}
      >
        <div
          className="absolute top-0 left-0 h-1 bg-succession-primary transition-all duration-700 ease-out"
          style={{ width: `${getOverallProgress()}%` }}
        ></div>

        {/* Modal Header with step indicator */}
        <div className="relative bg-succession-primary px-4 sm:px-6 py-4 text-white">
          <div className="relative flex items-center justify-between">
            <div className="space-y-2">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <span className="text-sm font-bold">{currentStep}</span>
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold">
                    {currentStep === 1
                      ? intl.formatMessage({ id: 'succession.buyer_room.proposal_form.step1_title' })
                      : intl.formatMessage({ id: 'succession.buyer_room.proposal_form.step2_title' })}
                  </h2>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    <p className="text-white/80 text-xs font-medium">
                      {intl.formatMessage(
                        { id: 'succession.buyer_room.proposal_form.step_progress' },
                        { current: currentStep, percent: Math.round(getStepProgress()) }
                      )}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Step indicator dots */}
              <div className="flex space-x-2">
                <div className={`w-8 h-1 rounded-full transition-all duration-300 ${
                  currentStep >= 1 ? 'bg-white' : 'bg-white/30'
                }`}></div>
                <div className={`w-8 h-1 rounded-full transition-all duration-300 ${
                  currentStep >= 2 ? 'bg-white' : 'bg-white/30'
                }`}></div>
              </div>
            </div>
            
            <button
              onClick={onCancel}
              className="group relative w-8 h-8 sm:w-10 sm:h-10 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-300"
              disabled={loading}
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Body - Scrollable with step content */}
        <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
          <div className="p-4 sm:p-6">
            
            {currentStep === 1 ? (
              // Step 1: Proposal and Meeting
              <div className="space-y-6">
                <div className="text-center space-y-3">
                  <div className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-50 to-purple-50 rounded-full border border-blue-100">
                    <svg className="w-3 h-3 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span className="text-xs font-semibold text-gray-700">
                      {intl.formatMessage({ id: 'succession.buyer_room.proposal_form.tell_us_about_yourself' })}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {intl.formatMessage({ id: 'succession.buyer_room.proposal_form.tell_us_about_yourself_description' })}
                  </p>
                </div>

                {/* Proposal Letter */}
                <div className="group relative">
                  <div className={`relative bg-gradient-to-br from-gray-50 to-white p-4 sm:p-5 rounded-xl border transition-all duration-300 ${
                    focusedField === 'proposal_letter' ? 'border-blue-300 shadow-lg shadow-blue-100 scale-[1.01]' : 'border-gray-200 hover:border-gray-300'
                  } ${errors.proposal_letter ? 'border-red-300 bg-red-50/50' : ''}`}>
                    
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                        focusedField === 'proposal_letter' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <label htmlFor="proposal_letter" className="block text-sm sm:text-base font-bold text-gray-900">
                          {intl.formatMessage({ id: 'succession.buyer_room.proposal_form.expression_of_interest_label' })}
                        </label>
                        <p className="text-xs sm:text-sm text-gray-500">
                          {intl.formatMessage({ id: 'succession.buyer_room.proposal_form.expression_of_interest_hint' })}
                        </p>
                      </div>
                    </div>
                    
                    <textarea
                      id="proposal_letter"
                      name="proposal_letter"
                      value={formData.proposal_letter}
                      onChange={handleChange}
                      onFocus={() => handleFocus('proposal_letter')}
                      onBlur={handleBlur}
                      rows={5}
                      className={`w-full px-3 py-3 border-0 bg-white/70 backdrop-blur-sm rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400 resize-none text-sm ${
                        errors.proposal_letter ? 'ring-2 ring-red-300' : ''
                      }`}
                      placeholder={intl.formatMessage({ id: 'succession.buyer_room.proposal_form.expression_of_interest_placeholder' })}
                    />
                    
                    <div className="flex justify-between items-center mt-2">
                      <div className="text-xs text-gray-400">
                        {intl.formatMessage(
                          { id: 'succession.buyer_room.proposal_form.character_count' },
                          { count: formData.proposal_letter.length }
                        )}
                      </div>
                      {errors.proposal_letter && (
                        <p className="text-xs text-red-600 flex items-center animate-in slide-in-from-right">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errors.proposal_letter}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Meeting Availability */}
                <div className="group relative">
                  <div className={`relative bg-gradient-to-br from-gray-50 to-white p-4 sm:p-5 rounded-xl border transition-all duration-300 ${
                    focusedField === 'proposal_meeting_availability' ? 'border-blue-300 shadow-lg shadow-blue-100 scale-[1.01]' : 'border-gray-200 hover:border-gray-300'
                  } ${errors.proposal_meeting_availability ? 'border-red-300 bg-red-50/50' : ''}`}>
                    
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                        focusedField === 'proposal_meeting_availability' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <label htmlFor="proposal_meeting_availability" className="block text-sm sm:text-base font-bold text-gray-900">
                          {intl.formatMessage({ id: 'succession.buyer_room.proposal_form.meeting_availability_label' })}
                        </label>
                        <p className="text-xs sm:text-sm text-gray-500">
                          {intl.formatMessage({ id: 'succession.buyer_room.proposal_form.meeting_availability_hint' })}
                        </p>
                      </div>
                    </div>
                    
                    <textarea
                      id="proposal_meeting_availability"
                      name="proposal_meeting_availability"
                      value={formData.proposal_meeting_availability}
                      onChange={handleChange}
                      onFocus={() => handleFocus('proposal_meeting_availability')}
                      onBlur={handleBlur}
                      rows={3}
                      className={`w-full px-3 py-3 border-0 bg-white/70 backdrop-blur-sm rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400 resize-none text-sm ${
                        errors.proposal_meeting_availability ? 'ring-2 ring-red-300' : ''
                      }`}
                      placeholder={intl.formatMessage({ id: 'succession.buyer_room.proposal_form.meeting_availability_placeholder' })}
                    />
                    
                    {errors.proposal_meeting_availability && (
                      <p className="mt-2 text-xs text-red-600 flex items-center animate-in slide-in-from-right">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.proposal_meeting_availability}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Step 2: Additional Questions
              <div className="space-y-6">
                <div className="text-center space-y-3">
                  <div className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-full border border-purple-100">
                    <svg className="w-3 h-3 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <span className="text-xs font-semibold text-gray-700">
                      {intl.formatMessage({ id: 'succession.buyer_room.proposal_form.tell_us_more' })}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {intl.formatMessage({ id: 'succession.buyer_room.proposal_form.tell_us_more_description' })}
                  </p>
                </div>

                <div className="space-y-4">
                  {getValidQuestions().map((field, index) => (
                    <div key={field.name} className="group">
                      <div className={`relative bg-white/70 backdrop-blur-sm p-4 rounded-xl border transition-all duration-300 ${
                        focusedField === field.name ? 'border-blue-300 shadow-lg shadow-blue-100 scale-[1.01]' : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                      } ${errors[field.name] ? 'border-red-300 bg-red-50/50' : ''}`}>
                        
                        <div className="flex items-start space-x-3 mb-3">
                          <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold transition-colors flex-shrink-0 mt-0.5 ${
                            focusedField === field.name ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {index + 1}
                          </div>
                          <label htmlFor={field.name} className="block text-xs sm:text-sm font-bold text-gray-900 flex-1 leading-tight">
                            {field.question} *
                          </label>
                        </div>
                        
                        <textarea
                          type="text"
                          id={field.name}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          onFocus={() => handleFocus(field.name)}
                          onBlur={handleBlur}
                          className={`w-full px-3 py-2.5 border-0 bg-white/70 backdrop-blur-sm rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400 text-sm ${
                            errors[field.name] ? 'ring-2 ring-red-300' : ''
                          }`}
                          placeholder={intl.formatMessage({ id: 'succession.buyer_room.proposal_form.answer_placeholder' })}
                        />
                        
                        {errors[field.name] && (
                          <p className="mt-2 text-xs text-red-600 animate-in slide-in-from-bottom">
                            {errors[field.name]}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer - Step navigation */}
        <div className="relative bg-succession-bg-secondary px-4 sm:px-6 py-4 border-t border-succession-border">
          <div className="flex items-center justify-between">
            {currentStep === 1 ? (
              <>
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={loading}
                  className="group relative bg-white text-gray-700 px-6 py-3 rounded-xl font-bold border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] text-sm"
                >
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {intl.formatMessage({ id: 'succession.buyer_room.proposal_form.cancel_button' })}
                  </span>
                </button>
                
                <button
                  type="button"
                  onClick={handleNextStep}
                  disabled={loading}
                  className="group relative bg-succession-primary text-white px-8 py-3 rounded-xl font-bold hover:opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transform hover:scale-[1.02] active:scale-[0.98] shadow-succession text-sm"
                >
                  <div className="flex items-center relative z-10">
                    <span>{intl.formatMessage({ id: 'succession.buyer_room.proposal_form.continue_button' })}</span>
                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handlePrevStep}
                  disabled={loading}
                  className="group relative bg-white text-gray-700 px-6 py-3 rounded-xl font-bold border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] text-sm"
                >
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    {intl.formatMessage({ id: 'succession.buyer_room.proposal_form.back_button' })}
                  </span>
                </button>
                
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="group relative bg-succession-primary text-white px-8 py-3 rounded-xl font-bold hover:opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transform hover:scale-[1.02] active:scale-[0.98] shadow-succession text-sm"
                >
                  {loading ? (
                    <div className="flex items-center relative z-10">
                      <div className="w-4 h-4 mr-3">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      </div>
                      <span>{intl.formatMessage({ id: 'succession.buyer_room.proposal_form.submitting' })}</span>
                    </div>
                  ) : (
                    <div className="flex items-center relative z-10">
                      <svg className="w-4 h-4 mr-2 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <span>{intl.formatMessage({ id: 'succession.buyer_room.proposal_form.submit_button' })}</span>
                    </div>
                  )}
                </button>
              </>
            )}
          </div>
          
          {/* Progress indicator */}
          <div className="mt-3 text-center">
            <div className="inline-flex items-center space-x-2 text-xs text-gray-500">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{intl.formatMessage({ id: 'succession.buyer_room.proposal_form.secure_processing' })}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

ProposalSubmissionForm.propTypes = {
  dealInterest: PropTypes.object.isRequired,
  deal: PropTypes.object.isRequired,
  onSubmitProposal: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  demoSecretMenuEnabled: PropTypes.bool
};

ProposalSubmissionForm.defaultProps = {
  loading: false,
  demoSecretMenuEnabled: false
};

export default ProposalSubmissionForm; 